import * as angular from 'angular';
import * as _ from 'lodash';

import { Group, ITreeConfiguration, ITreeItem, Pattern } from '../../models';
import { momentFormat } from '../../common';
import {
    IGroupService,
    IThermoPackageService,
    ITagDefinitionService,
    ITrendService,
    IValidationPageConfigurationService,
} from '../data';
import { INavigationHistoryService } from './navigationHistory.service';

export interface IstateDependencyService {
    getItems(state: angular.ui.IState): angular.IPromise<ITreeConfiguration>;
    getItemsByName(stateName: string, subState: string): angular.IPromise<ITreeConfiguration>;
}

export class StateDependencyService implements IstateDependencyService {
    static serviceId = 'stateDependencyService';

    static $inject = [
        '$q',
        '$translate',
        'groupService',
        'thermoPackageService',
        'tagDefinitionService',
        'trendService',
        'validationPageConfigurationService',
        'navigationHistoryService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _$translate: angular.translate.ITranslateService,
        private _groupService: IGroupService,
        private _thermoPackageService: IThermoPackageService,
        private _tagDefinitionService: ITagDefinitionService,
        private _trendService: ITrendService,
        private _validationPageConfigurationService: IValidationPageConfigurationService,
        private _navigationHistoryService: INavigationHistoryService
    ) {
    }

    getItemsByName(stateName: string, subState: string): angular.IPromise<ITreeConfiguration> {
        const defered = this._$q.defer<ITreeConfiguration>();
        const configuration: ITreeConfiguration = {
            url: subState,
            items: [],
            urlParams: {}
        };
        switch (stateName) {
            case 'home':
                this.createHomeList(configuration, defered);
                break;
            case 'identifiers':
            case 'models.structure':
                this.createModelList(configuration, defered);
                break;
            case 'dashboards':
                break;
            case 'thermoPackages':
                this.createThermoList(configuration, defered);
                break;
            case 'trends':
                this.createTrendList(configuration, defered);
                break;
            case 'manualInputs':
            case 'dataEntry':
                this.createManualInputList(configuration, defered);
                break;
            case 'tags':
            case 'tagsTable':
            case 'tag.general':
            case 'spotValues':
                configuration.items = [];
                defered.resolve(configuration);
                break;
            default:
                throw new Error(`Invalid state name: ${stateName}`);
        }
        return defered.promise;
    }

    getItems(state: angular.ui.IState): angular.IPromise<ITreeConfiguration> {
        return this.getItemsByName(state.name, state.data.subMenu.subState);
    }

    private createHomeList(configuration: ITreeConfiguration, defered: angular.IDeferred<ITreeConfiguration>) {
        configuration.items = this._navigationHistoryService.getNavigationHistories().map((h, i) => {
            const name = this._$translate.instant(h.name);
            const dates = h.interval.begin && h.interval.end
                ? `${h.interval.begin.format(momentFormat)}  - ${h.interval.end.format(momentFormat)}`
                : undefined;
            return {
                depth: 1,
                name: name,
                itemType: undefined,
                comment: undefined,
                children: undefined,
                _children: undefined,
                isVisible: true,
                guid: Number(i).toString(),
                index: i,
                title: `${name}\n${dates}`,
                icon: h.icon,
                parentGUID: '',
                url: ''
            };
        });
        configuration.url = 'navigationHistory';
        configuration.urlParams = { index: (item: ITreeItem) => (item as any).index };
        defered.resolve(configuration);
    }

    private createModelList(configuration: ITreeConfiguration, defered: angular.IDeferred<ITreeConfiguration>) {
        this._groupService.getAllAsTree()
            .then(items => {
                configuration.items = items;
                items.forEach(i => { i.title = i.comment ? i.comment : i.name; });
                configuration.urlParams = { groupGuid: (item: ITreeItem) => item.guid };
                defered.resolve(configuration);
            });
    }

    private createThermoList(configuration: ITreeConfiguration, defered: angular.IDeferred<ITreeConfiguration>) {
        this._thermoPackageService.getAll()
            .then(thermoPackages => {
                const items: any[] = _.map(thermoPackages, 'packageInformation');
                items.forEach(t => {
                    t.depth = 1;
                    t.isVisible = true;
                });
                configuration.items = items;
                configuration.urlParams = { name: (item: ITreeItem) => item.name };
                defered.resolve(configuration);
            });
    }

    private createTrendList(configuration: ITreeConfiguration, defered: angular.IDeferred<ITreeConfiguration>) {
        this._trendService.getAll().then(trends => {
            (trends as any[]).forEach(t => {
                t.isVisible = true;
                t.depth = 1;
                t.title = t.name;
            });
            configuration.items = (trends as any[]);
            configuration.urlParams = { trendGuid: (item: ITreeItem) => item.guid };
            defered.resolve(configuration);
        });
    }

    private createManualInputList(configuration: ITreeConfiguration, defered: angular.IDeferred<ITreeConfiguration>) {
        this._validationPageConfigurationService
            .getValidationTreeGroups()
            .then(groups => {
                configuration.items = groups.map(g => {
                    return {
                        _children: undefined,
                        children: undefined,
                        comment: g.shortComment,
                        depth: 1,
                        guid: g.guid,
                        isVisible: true,
                        item: g,
                        itemType: 'group',
                        name: g.uniqueName,
                        parentGUID: '',
                        title: g.uniqueName,
                        url: ''
                    };
                });
                configuration.urlParams = {
                    groupGuid: (item: ITreeItem) => item.guid,
                    beginDate: () => null
                };
                const searchCriteria: Pattern<ITreeItem<Group>>[] = [
                    {
                        placeholder: 'Search pattern on group',
                        label: 'Group',
                        model: 'g',
                        property: 'g',
                        isMatch: (item, reg) => {
                            return reg.test(item.item.uniqueName);
                        }
                    },
                    {
                        placeholder: 'Search pattern on group type',
                        label: 'Group types',
                        model: 'gt',
                        property: 'gt',
                        isMatch: (item, reg) => {
                            return reg.test(item.item.type);
                        }
                    }, {
                        placeholder: 'Search pattern on comment',
                        label: 'Comment',
                        model: 'c',
                        property: 'c',
                        isMatch: (item, reg) => {
                            return reg.test(item.item.shortComment) || reg.test(item.item.longComment);
                        }
                    }
                ];
                configuration.searchCriteria = searchCriteria;
                configuration.searchPlaceholder = 'Search for groups (g:group c:comment gt:type)';
                defered.resolve(configuration);
            });
    }
}